/**
 * Product Domain Resolver — Single Source of Truth (Phase 2C Stabilized)
 *
 * PURE FUNCTION: (product) → ResolvedProduct
 * Zero side effects. Zero API calls. Zero async. Zero external fetch.
 *
 * ═══════════════════════════════════════════════════════════════
 * SUPPLY_PRIORITY_RULE (immutable):
 *   1. distributions[]       → multi-supply truth layer
 *   2. product.partner       → legacy single-supplier fallback
 *   3. mode "none"           → no supply data available
 * ═══════════════════════════════════════════════════════════════
 *
 * PRICING_LOCK_RULE (immutable):
 *   1. distributions[].purchase_price  → source prix d'achat (PartnerDashboard, AdminProducts)
 *   2. product.prix_vente_conseille    → PVC affiché directement dans ProductCard/ProductDetailPage
 *   3. "Prix NC" affiché dans l'UI si source absente
 * ═══════════════════════════════════════════════════════════════
 *
 * DISTRIBUTORS_DETERMINISM_RULE (immutable):
 *   primary = lowest purchase_price distributor if any price exists
 *           | first distribution (index 0) if all prices null
 *           | legacy partner if no distributions
 *   NEVER random. NEVER based on external API.
 * ═══════════════════════════════════════════════════════════════
 *
 * Feature flags:
 *   window.__PRODUCT_RESOLVER_DEBUG__          — verbose log per product
 *   window.__PRODUCT_DOMAIN_CONSISTENCY__      — warn on data inconsistencies
 *   window.__USE_PRODUCT_RESOLVER_PRICING__    — activate pricing resolver in UI
 *   window.__USE_PRODUCT_RESOLVER_DISTRIBUTORS__ — activate distributors resolver in UI
 */

// ─── TYPES ──────────────────────────────────────────────────

/**
 * @typedef {Object} ResolvedPricing
 * @property {'single'|'multi'|'none'} mode
 * @property {number|null} price - Resolved price (lowest if multi)
 * @property {string} priceLabel - Formatted label for display
 * @property {boolean} hasAnyPrice
 * @property {'distributions'|'legacy'|'none'} source - Where the price came from
 */

/**
 * @typedef {Object} ResolvedDistributors
 * @property {'single'|'multi'|'none'} mode
 * @property {Object|null} primary - Primary distributor (deterministic selection)
 * @property {Array} list - All resolved distributor objects
 * @property {string} displayLabel - UI label
 * @property {'distributions'|'legacy'|'none'} source - Where the data came from
 */

/**
 * @typedef {Object} ResolvedEditors
 * @property {'single'|'multi'|'none'} mode
 * @property {Object|null} primary - Primary editor
 * @property {Array} list - All editor objects (enriched)
 * @property {string} displayLabel - UI label
 * @property {'editors'|'legacy'|'none'} source - Where the data came from
 */

/**
 * @typedef {Object} ResolvedProduct
 * @property {ResolvedPricing} pricing
 * @property {ResolvedDistributors} distributors
 * @property {ResolvedEditors} editors
 * @property {Object} legacy - Raw legacy fields for reference
 * @property {string} supplyMode - 'distributions'|'legacy'|'hybrid'|'none'
 */

// ─── CONSTANTS ──────────────────────────────────────────────

const SUPPLY_SOURCE = Object.freeze({
  DISTRIBUTIONS: 'distributions',
  EDITORS: 'editors',
  ORIGINAL_PUBLISHERS: 'original_publishers',
  LEGACY: 'legacy',
  NONE: 'none'
});

const NULL_PRICING = Object.freeze({
  mode: 'none', price: null, priceLabel: 'Prix NC',
  hasAnyPrice: false, source: SUPPLY_SOURCE.NONE
});

const NULL_DISTRIBUTORS = Object.freeze({
  mode: 'none', primary: null, list: [],
  displayLabel: '\u2014', source: SUPPLY_SOURCE.NONE
});

const NULL_EDITORS = Object.freeze({
  mode: 'none', primary: null, list: [],
  displayLabel: '\u2014', source: SUPPLY_SOURCE.NONE
});

// ─── PRICING (LOCKED) ──────────────────────────────────────

function resolvePricing(product) {
  const distributions = product.distributions || [];
  const validPrices = distributions
    .map(d => d.purchase_price)
    .filter(p => p != null && typeof p === 'number');

  // PRICING_LOCK_RULE: distributions[] → source prix d'achat
  // Note: ProductCard et ProductDetailPage n'utilisent PAS ce resolver pour le prix principal.
  // Ils affichent directement product.prix_vente_conseille (PVC).
  // Ce resolver reste utilisé par PartnerDashboard, PartnerProducts, AdminProducts.
  if (validPrices.length === 1) {
    return {
      mode: 'single',
      price: validPrices[0],
      priceLabel: `${validPrices[0].toFixed(2)}€`,
      hasAnyPrice: true,
      source: SUPPLY_SOURCE.DISTRIBUTIONS
    };
  }

  if (validPrices.length > 1) {
    const minPrice = Math.min(...validPrices);
    return {
      mode: 'multi',
      price: minPrice,
      priceLabel: `À partir de ${minPrice.toFixed(2)}€`,
      hasAnyPrice: true,
      source: SUPPLY_SOURCE.DISTRIBUTIONS
    };
  }

  return { ...NULL_PRICING };
}

// ─── DISTRIBUTORS (DETERMINISTIC) ───────────────────────────

function resolveDistributors(product) {
  const distributions = product.distributions || [];

  // SUPPLY_PRIORITY_RULE: Level 1 — distributions[]
  if (distributions.length > 0) {
    // Build partner lookup from product.partner (only known enriched partner)
    const partnerMap = {};
    if (product.partner) {
      partnerMap[product.partner.id] = product.partner;
    }

    // Enrich each distribution with partner info (pure — no API calls)
    // PRIORITY: partnerMap (enriched partner object) > distribution-level enrichment fields > generic fallback
    const enrichedList = distributions.map(d => ({
      ...d,
      partner: partnerMap[d.partner_id] || {
        id: d.partner_id,
        nom: d.partner_nom || d.partner_name || 'Distributeur',
        slug: d.partner_slug || null,
        is_partner: d.partner_is_commercial || false,
        capabilities: d.partner_capabilities || []
      }
    }));

    if (enrichedList.length === 1) {
      const primary = enrichedList[0].partner;
      return {
        mode: 'single',
        primary,
        list: [primary],
        displayLabel: primary.nom || '\u2014',
        source: SUPPLY_SOURCE.DISTRIBUTIONS
      };
    }

    // DISTRIBUTORS_DETERMINISM_RULE: multi
    // Step 1: filter distributions with valid prices
    const withPrice = enrichedList.filter(d => d.purchase_price != null);
    // Step 2: sort by price ASC (deterministic — stable sort)
    const sorted = withPrice.length > 0
      ? [...withPrice].sort((a, b) => a.purchase_price - b.purchase_price)
      : enrichedList; // All null prices → use original order (index 0 = primary)
    // Step 3: primary = first of sorted (lowest price or first element)
    const primary = sorted[0].partner;
    const allPartners = enrichedList.map(d => d.partner);

    return {
      mode: 'multi',
      primary,
      list: allPartners,
      displayLabel: 'Plusieurs distributeurs',
      source: SUPPLY_SOURCE.DISTRIBUTIONS
    };
  }

  // SUPPLY_PRIORITY_RULE: Level 2 — no distributions
  // Legacy partner fallback SUPPRIMÉ (P2 cleanup)
  // product.partner_id n'est plus utilisé comme source de distributeur
  return { ...NULL_DISTRIBUTORS };
}

// ─── EDITORS (Phase 3 — Safe Switch) ────────────────────────

function resolveEditors(product) {
  // ── PRIORITY 1: ProductPublisher (structured N-N — UNIFIED EDITORIAL MODEL) ──
  const originalPublishers = product.original_publishers || product.publishers || [];
  if (originalPublishers.length > 0) {
    // All publishers are equal — coédition native, no role-based sorting
    const primary = originalPublishers[0];
    const primaryId = primary.partner_id || primary.original_publisher_id;
    
    if (originalPublishers.length === 1) {
      return {
        mode: 'single',
        primary: { id: primaryId, nom: primary.name },
        list: originalPublishers,
        displayLabel: primary.name || '\u2014',
        source: SUPPLY_SOURCE.ORIGINAL_PUBLISHERS,
        originalPublishers,
      };
    }
    
    // Coédition — all publishers are equal
    return {
      mode: 'multi',
      primary: { id: primaryId, nom: primary.name },
      list: originalPublishers,
      displayLabel: originalPublishers.map(p => p.name).filter(Boolean).join(' / '),
      source: SUPPLY_SOURCE.ORIGINAL_PUBLISHERS,
      originalPublishers,
    };
  }

  // ── PRIORITY 2: Fallback to editors[] (backward compat — same data as publishers) ──
  const editors = product.editors || [];

  if (editors.length > 0) {
    const enrichedList = editors.map(e => ({
      ...e,
      partner: e.partner || { id: e.partner_id, nom: e.name || e.partner_nom || '\u00c9diteur' }
    }));

    const primary = enrichedList[0].partner || enrichedList[0];
    if (enrichedList.length === 1) {
      return {
        mode: 'single', primary,
        list: enrichedList,
        displayLabel: primary.nom || primary.name || '\u2014',
        source: SUPPLY_SOURCE.EDITORS
      };
    }
    return {
      mode: 'multi', primary,
      list: enrichedList,
      displayLabel: enrichedList.map(e => e.name || e.partner?.nom || '').filter(Boolean).join(' / ') || 'Plusieurs \u00e9diteurs',
      source: SUPPLY_SOURCE.EDITORS
    };
  }

  // No publisher data found
  return { ...NULL_EDITORS };
}

// ─── SUPPLIER REFERENCES (P1 — resolver-first) ─────────────

function resolveSupplierReferences(product, distributors) {
  const distributions = product.distributions || [];
  const all = distributions
    .filter(d => d.supplier_reference)
    .map(d => ({ partner_id: d.partner_id, supplier_reference: d.supplier_reference }));
  const primary = distributors.primary
    ? (distributions.find(d => d.partner_id === distributors.primary.id)?.supplier_reference || null)
    : null;
  // Fallback to legacy ref_distributeur if no distribution refs
  const legacyRef = product.ref_distributeur || null;
  return {
    primary: primary || legacyRef,
    all,
    source: all.length > 0 ? SUPPLY_SOURCE.DISTRIBUTIONS : (legacyRef ? SUPPLY_SOURCE.LEGACY : SUPPLY_SOURCE.NONE)
  };
}

// ─── AVAILABILITY — SUPPRIMÉ (disponibilité uniquement au niveau distribution)
// resolveAvailability() a été supprimée.
// La disponibilité est lue directement depuis product.distributions[].availability_status.
// Aucun resolver global de disponibilité produit n'est autorisé.

// ─── SUPPLY MODE ────────────────────────────────────────────

function computeSupplyMode(product) {
  const hasDist = (product.distributions || []).length > 0;
  const hasLegacy = Boolean(product.partner);
  if (hasDist && hasLegacy) return 'hybrid';
  if (hasDist) return 'distributions';
  if (hasLegacy) return 'legacy';
  return 'none';
}

// ─── CONSISTENCY CHECK (debug only) ─────────────────────────

function checkConsistency(product, pricing, distributors, editors) {
  if (typeof window === 'undefined' || !window.__PRODUCT_DOMAIN_CONSISTENCY__) return;

  const warnings = [];
  const pid = product.id || '?';
  const distributions = product.distributions || [];
  const editorsList = product.editors || [];

  // Rule 1: distributions pricing consistency
  if (distributions.length > 0) {
    const distPrices = distributions
      .map(d => d.purchase_price)
      .filter(p => p != null && typeof p === 'number');
    if (distPrices.length > 0 && distPrices.length < distributions.length) {
      warnings.push(`partial pricing: ${distPrices.length}/${distributions.length} distributions have prices`);
    }
  }

  // Rule 2: partner absent but distributions exist
  if (!product.partner && distributions.length > 0) {
    warnings.push('product.partner is null but distributions[] has entries');
  }

  // Rule 3: inconsistent null/non-null prices in distributions
  if (distributions.length > 1) {
    const nullCount = distributions.filter(d => d.purchase_price == null).length;
    const nonNullCount = distributions.length - nullCount;
    if (nullCount > 0 && nonNullCount > 0) {
      warnings.push(`mixed null/non-null prices: ${nonNullCount} priced, ${nullCount} null`);
    }
  }

  // Rule 4: distributions exist but pricing resolved from legacy
  if (distributions.length > 0 && pricing.source === SUPPLY_SOURCE.LEGACY) {
    warnings.push('distributions exist but pricing resolved from legacy (all dist prices null)');
  }

  // Rule 5: legacy partner not in distributions
  if (product.partner && distributions.length > 0) {
    const partnerInDist = distributions.some(d => d.partner_id === product.partner.id);
    if (!partnerInDist) {
      warnings.push(`legacy partner ${product.partner.nom} (${product.partner.id}) not found in distributions[]`);
    }
  }

  // Rule 6: publishers exist — no legacy check needed (UNIFIED MODEL)

  // Rule 7: no publishers found (expected for products without publisher data)

  if (warnings.length > 0) {
    console.warn(`[PRODUCT_DOMAIN_CONSISTENCY] ${pid} (${product.intitule || '?'}):`, warnings);
  }
}

// ─── MAIN RESOLVER ──────────────────────────────────────────

/**
 * Resolves all business data for a product.
 * PURE FUNCTION: no side effects, no API calls, no async.
 *
 * @param {Object} product - Enriched product object (API response)
 * @returns {ResolvedProduct}
 */
export function resolveProduct(product) {
  if (!product) {
    return {
      pricing: { ...NULL_PRICING },
      distributors: { ...NULL_DISTRIBUTORS },
      editors: { ...NULL_EDITORS },
      legacy: { partnerId: null, editeur: null },
      supplyMode: 'none'
    };
  }

  const pricing = resolvePricing(product);
  const distributors = resolveDistributors(product);
  const editors = resolveEditors(product);
  const supplierReferences = resolveSupplierReferences(product, distributors);
  const supplyMode = computeSupplyMode(product);

  const resolved = {
    pricing,
    distributors,
    editors,
    supplierReferences,
    legacy: {
      partnerId: product.partner_id || null,
      refDistributeur: product.ref_distributeur || null
    },
    supplyMode
  };

  // Consistency check (debug only — never in production)
  checkConsistency(product, pricing, distributors);

  // Debug mode
  if (typeof window !== 'undefined' && window.__PRODUCT_RESOLVER_DEBUG__) {
    console.log('[ProductResolver]', {
      productId: product.id,
      intitule: product.intitule,
      supplyMode,
      pricingSource: pricing.source,
      distributorsSource: distributors.source,
      ...resolved
    });
  }

  return resolved;
}

export default resolveProduct;
